/**
 * CORE-879 — Live-сессия внутри программы: правила формы и поведение календаря.
 *
 * Модуль без зависимостей от React Native намеренно: здесь живут решения,
 * которые нужно проверять тестами, а не глазами. Под jest в этом репозитории
 * компоненты RN не импортируются, и правила, спрятанные внутри JSX, остались бы
 * непокрытыми.
 */

/** Что мастер вводит, заводя Live-сессию прямо в разделе программы. */
export interface ProgramLiveDraft {
  title: string;
  /** Локальная дата-время из формы, `YYYY-MM-DDTHH:mm`. */
  startsAtLocal: string;
  durationMinutes: string;
  description: string;
  /** По умолчанию Live-сессия видна только купившим — мастер заводит его в платном продукте. */
  visibility: 'program' | 'all' | 'subscribers';
}

export function emptyProgramLiveDraft(): ProgramLiveDraft {
  return {
    title: '',
    startsAtLocal: '',
    durationMinutes: '',
    description: '',
    // Мастер, который заводит Live-сессию из редактора платной программы, продаёт её,
    // а не публикует. Открытая остаётся возможной — но выбором, а не
    // умолчанием, о котором легко не подумать.
    visibility: 'program',
  };
}

export type ProgramLiveField = 'title' | 'startsAtLocal' | 'durationMinutes';

/**
 * Проверка формы до отправки.
 *
 * Дублирует правила бэкенда сознательно и ровно в том же объёме: смысл не в
 * том, чтобы заменить серверную проверку, а в том, чтобы не гонять человека
 * за ответом на очевидное. Всё, чего здесь нет, покажет ответ сервера — его
 * текст и показываем, свой не сочиняем.
 *
 * @param now — момент отсчёта, передаётся снаружи: время в тестах должно быть
 *   заданным, а не сегодняшним.
 */
export function validateProgramLive(
  draft: ProgramLiveDraft,
  now: Date,
): Partial<Record<ProgramLiveField, string>> {
  const errors: Partial<Record<ProgramLiveField, string>> = {};

  if (draft.title.trim() === '') {
    errors.title = 'У Live-сессии должно быть название';
  }

  if (draft.startsAtLocal.trim() === '') {
    errors.startsAtLocal = 'Укажите дату и время';
  } else {
    const at = new Date(draft.startsAtLocal);
    if (Number.isNaN(at.getTime())) {
      errors.startsAtLocal = 'Не получилось прочитать дату';
    } else if (at.getTime() <= now.getTime()) {
      // Прошедшая дата — это не Live-сессия, а запись: провести её уже нельзя,
      // а покупатель увидел бы в программе событие, которого не было.
      errors.startsAtLocal = 'Live-сессию можно назначить только на будущее';
    }
  }

  const duration = draft.durationMinutes.trim();
  if (duration !== '') {
    const minutes = Number(duration);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 24 * 60) {
      errors.durationMinutes = 'От 1 минуты до 24 часов';
    }
  }

  return errors;
}

export function hasErrors(errors: Partial<Record<ProgramLiveField, string>>): boolean {
  return Object.keys(errors).length > 0;
}

/** Тело запроса на создание Live-сессии. Вызывать только на прошедшем проверку черновике. */
export function toCreateRequest(draft: ProgramLiveDraft): {
  title: string;
  startDateTimeUtc: string;
  durationMinutes?: number;
  description?: string;
  visibility: string;
} {
  const duration = draft.durationMinutes.trim();
  const description = draft.description.trim();

  return {
    title: draft.title.trim(),
    // Человек вводит время в своём часовом поясе, сервер хранит в UTC.
    // Преобразование делает сам Date — вычитать смещение руками нельзя,
    // это ломается на переходе на летнее время.
    startDateTimeUtc: new Date(draft.startsAtLocal).toISOString(),
    ...(duration === '' ? {} : { durationMinutes: Number(duration) }),
    ...(description === '' ? {} : { description }),
    visibility: draft.visibility,
  };
}

// ─── Календарь купившего ───

/**
 * Можно ли убрать событие из личного календаря.
 *
 * Live-сессию купленной программы убрать нельзя, и это не запрет ради запрета: он
 * лежит в календаре по факту покупки, а не по нажатой кнопке «Напомнить».
 * Строки подписки под ним нет вовсе — нажатие «убрать» ничего бы не изменило,
 * а человек решил бы, что кнопка сломана.
 */
export function canLeaveCalendarEvent(item: { byProgram?: boolean | null }): boolean {
  return item.byProgram !== true;
}

/**
 * Подпись, объясняющая, почему событие в календаре и почему его не убрать.
 *
 * Пусто для обычных событий: у них причина очевидна — человек сам нажал.
 */
export function calendarSourceNote(item: { byProgram?: boolean | null }): string {
  return item.byProgram === true ? 'Входит в купленную программу' : '';
}

/**
 * Лежит ли Live-сессия в личном календаре по купленной программе.
 *
 * Отвечает страница сессии: у такой «Присоединиться» не переключается — под
 * ней нет строки подписки, а нажатие завело бы ручную, и сервер перестал бы
 * считать сессию программной (в календаре `byProgram` сменился бы на `false`).
 */
export function isInCalendarByProgram(
  calendar: ReadonlyArray<{ id: number; byProgram?: boolean | null }>,
  liveId: number,
): boolean {
  return calendar.some(i => i.id === liveId && i.byProgram === true);
}
